// Export-Bar — three buttons under the Timeline for rendering the active
// asset's audio-only / mixed / video outputs. Ported from legacy
// `.timeline-export-bar` (index.html lines 2321–2341, CSS lines 1379–1421).
//
// Design parity:
//   - Three `.timeline-export-btn` buttons (Voice-Only WAV, Mixed WAV,
//     Video+Audio MP4). The MP4 button hides when WebCodecs/MP4Box/Mp4Muxer
//     aren't all present (Firefox → no button at all).
//   - A single `.timeline-export-status` span to the right of the buttons,
//     shows the current pipeline stage while an export is in flight and a
//     terminal "Downloaded …" / "Failed …" afterwards.
//   - Spinner (`ph-bold ph-circle-notch` + CSS rotation) visible only while
//     busy; "error" colors the status text red, "done" colors it green.
//   - All three buttons disabled while any one export is running (single
//     in-flight mutex in `useExport`).
//
// Wiring:
//   - Reads `currentAssetIdx` from the session store and passes it into
//     `useExport`'s handlers.
//   - Status auto-clears after 4s on terminal success / 2s on cancel, so
//     the UI doesn't permanently display stale messages. Mirrors legacy
//     `setTimeout(() => _setExportStatus(''), 4000)` pattern.

import { useEffect, useMemo } from 'react';
import {
  applyPredefinedFreeVoiceFallback,
  useSessionStore,
} from '@/stores/session';
import { useVideoStore } from '@/stores/video';
import { useExport } from '@/hooks/useExport';
import { hasVideoExportSupport } from '@/lib/videoExport';
import { useToast } from '@/components/ToastProvider';
import { ExportProgressModal } from '@/components/ExportProgressModal/ExportProgressModal';
import { resetPositionsPerTrack } from '@/lib/timeline';
import styles from './ExportBar.module.css';

interface Props {
  /** Optional override for the Reset Positions click. When provided, the
   *  parent (TTSView) handles reorder + reflow + activeBlockIdx remap. The
   *  fallback below still works for any consumer that doesn't pass this. */
  onResetPositions?: () => void;
}

function isTerminalStatus(status: string): 'done' | 'error' | null {
  if (!status) return null;
  if (status.startsWith('Downloaded ') || status.startsWith('Saved ')) return 'done';
  if (
    status.startsWith('Failed') ||
    status.startsWith('No timeline') ||
    status.startsWith('No favorites') ||
    status.startsWith('No clips') ||
    status.startsWith('No video') ||
    status.startsWith('Video export not supported') ||
    status === 'Cancelled'
  )
    return 'error';
  return null;
}

export function ExportBar({ onResetPositions: onResetPositionsProp }: Props = {}) {
  const currentAssetIdx = useSessionStore((s) => s.currentAssetIdx);
  const activeBlocks = useSessionStore(
    (s) => s.assetBlocks[s.currentAssetIdx] ?? s.blocks,
  );
  // STS-mode detection: explicit `assetType === 'sts'` OR a free / custom
  // asset whose freeMode pill is 'sts'. Mirrors the same dispatch
  // `snapshotExportInput` uses so the button gates and the export pipeline
  // agree on which timeline drives readiness. Subscribe to the underlying
  // slices individually — `selectAllAssets` returns a fresh array each
  // call so subscribing through it would trip React's getSnapshot cache
  // (mirrors FreeChoiceView's approach).
  const configAssets = useSessionStore((s) => s.config?.assets);
  const configVoices = useSessionStore((s) => s.config?.voices);
  const customAssets = useSessionStore((s) => s.customAssets);
  const freeMode = useSessionStore((s) => s.freeMode);
  const isStsActive = useMemo(() => {
    const cfgWithFallback = applyPredefinedFreeVoiceFallback(
      configAssets,
      configVoices,
    );
    const all = [...cfgWithFallback, ...customAssets];
    const asset = all[currentAssetIdx];
    if (!asset) return false;
    if (asset.assetType === 'sts') return true;
    return asset.assetType === 'free' && freeMode[currentAssetIdx] === 'sts';
  }, [configAssets, configVoices, customAssets, freeMode, currentAssetIdx]);
  const stsMasterCount = useSessionStore(
    (s) => s.stsAssets[s.currentAssetIdx]?.master.length ?? 0,
  );
  // `hasVideo` — true when EITHER the user has uploaded a video for the
  // active asset (videoStore.sources/blobs) OR the admin has shipped a
  // reference video via asset.videoPath (which the VideoPlayer streams
  // directly via streamVideoUrl, never populating the videoStore). Without
  // the videoPath check the Mixed WAV + Video MP4 buttons stay hidden on
  // admin-video assets even though the export pipeline already resolves
  // them through snapshotExportInput.
  const sourceMap = useVideoStore((s) => s.sources);
  const blobMap = useVideoStore((s) => s.blobs);
  const hasVideo = useMemo(() => {
    if (sourceMap[currentAssetIdx] || blobMap[currentAssetIdx]) return true;
    const cfgWithFallback = applyPredefinedFreeVoiceFallback(
      configAssets,
      configVoices,
    );
    const all = [...cfgWithFallback, ...customAssets];
    const asset = all[currentAssetIdx];
    return !!asset?.videoPath;
  }, [sourceMap, blobMap, currentAssetIdx, configAssets, configVoices, customAssets]);
  const hasSfxBed = useSessionStore(
    (s) => (s.sfxBeds[currentAssetIdx]?.length ?? 0) > 0,
  );
  const toast = useToast();
  const {
    busy,
    status,
    warning,
    exportVoiceOnly,
    exportMixed,
    exportVideoMp4,
    clearStatus,
  } = useExport();

  // Reset is only meaningful for the TTS chip timeline (STS has no
  // chips — the master timeline reorders via direct drag). For STS we
  // keep the button disabled regardless of any stale assetBlocks data
  // that might have been left behind by a prior TTS-mode session on a
  // free-choice asset.
  const hasFavorites = useMemo(
    () =>
      !isStsActive &&
      activeBlocks.some((b) => b.favIdx >= 0 && b.takes[b.favIdx]),
    [activeBlocks, isStsActive],
  );

  // Export-button readiness: TTS needs at least one favorited take in the
  // block list; STS needs at least one master clip on the timeline.
  // Prevents clicking through to a "No clips / No favorites" toast and
  // also makes the legacy-parity disabled state visible.
  const hasExportableContent = isStsActive
    ? stsMasterCount > 0
    : hasFavorites;

  const onResetPositions = () => {
    if (busy) return;
    if (onResetPositionsProp) {
      onResetPositionsProp();
      return;
    }
    // Fallback path (no parent override): per-Voice-Spur sequenzielles
    // Reflow. Bei Multi-Voice (#173) sind die Spuren unabhängig — Cross-
    // Voice-Chips dürfen denselben Offset haben. Block-Array-Order bleibt
    // erhalten; daher braucht der Fallback auch keinen activeBlockIdx-Remap.
    const ok = window.confirm(
      'Reset all block positions to sequential left-to-right? Each voice-track starts at t=0 independently.',
    );
    if (!ok) return;
    const cfgWithFallback = applyPredefinedFreeVoiceFallback(
      configAssets,
      configVoices,
    );
    const all = [...cfgWithFallback, ...customAssets];
    const asset = all[currentAssetIdx];
    const voiceIds = asset?.voiceIds ?? [];
    useSessionStore.getState().updateActiveBlocks((prev) =>
      resetPositionsPerTrack(prev, voiceIds),
    );
    toast.show('Block positions reset', 2000);
  };

  // Feature-detect once per mount — the vendored scripts in index.html are
  // loaded synchronously before React renders so this evaluates the same on
  // every call, but memoizing avoids re-reading window.* on every render.
  const canExportVideo = useMemo(() => hasVideoExportSupport(), []);

  // Auto-clear terminal status messages so the bar doesn't show stale text.
  // The effect re-runs whenever `status` changes (new terminal status → new
  // timer; new in-flight status → cleanup cancels the pending clear).
  const terminal = isTerminalStatus(status);
  useEffect(() => {
    if (!terminal) return;
    const delay = status === 'Cancelled' ? 2000 : 4000;
    const tid = window.setTimeout(() => clearStatus(), delay);
    return () => window.clearTimeout(tid);
  }, [terminal, status, clearStatus]);

  // Hinweis des letzten Exports (z. B. Video-Ton fehlt im Premix, Audit M18)
  // als Toast — länger sichtbar als die Statuszeile.
  useEffect(() => {
    if (warning) toast.show(warning, 8000);
  }, [warning, toast]);

  const statusCls = [
    styles.status,
    busy ? styles.busy : '',
    terminal === 'done' ? styles.done : '',
    terminal === 'error' ? styles.error : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.bar}>
      <button
        type="button"
        className={styles.btn}
        onClick={() => void exportVoiceOnly(currentAssetIdx)}
        disabled={busy || !hasExportableContent}
        title="Export voice timeline as WAV (raw, full amplitude)"
      >
        <i className="ph-bold ph-microphone" />
        <span>Voice-Only WAV</span>
      </button>
      {(hasVideo || hasSfxBed) && (
        <button
          type="button"
          className={styles.btn}
          onClick={() => void exportMixed(currentAssetIdx)}
          disabled={busy || !hasExportableContent}
          title="Export mixed audio (voice + video, with mixer + ducking)"
        >
          <i className="ph-bold ph-music-notes" />
          <span>Mixed WAV</span>
        </button>
      )}
      {canExportVideo && hasVideo && (
        <button
          type="button"
          className={styles.btn}
          onClick={() => void exportVideoMp4(currentAssetIdx)}
          disabled={busy || !hasExportableContent}
          title="Export video with new mixed audio track (MP4)"
        >
          <i className="ph-bold ph-video" />
          <span>Video + Audio MP4</span>
        </button>
      )}
      {/* While busy: just a spinner — the intermediate status texts
       *  (Rendering audio…, Encoding AAC…, Muxing MP4…) are technical
       *  and not actionable. Show them as the spinner alone. After the
       *  pipeline lands a terminal state the original status string
       *  ("Downloaded {filename}", "Failed: …", "Cancelled") is
       *  surfaced so the user gets the result. */}
      <span className={statusCls} aria-live="polite">
        {busy ? (
          <i
            className={`ph-bold ph-circle-notch ${styles.spinner}`}
            aria-label="Exporting…"
          />
        ) : (
          <span className={styles.statusText}>{status}</span>
        )}
      </span>
      <button
        type="button"
        className={styles.btn}
        onClick={onResetPositions}
        disabled={busy || !hasFavorites}
        title="Reset all block positions to sequential left-to-right (chip order preserved)"
      >
        <i className="ph-bold ph-clock-counter-clockwise" />
        <span>Reset Positions</span>
      </button>
      {/* Full-screen non-dismissible overlay while an export is running.
       *  Min-1s display floor so fast voice-only WAV exports don't flash.
       *  Live status (Rendering audio…, Encoding video…, …) shown as a
       *  secondary line for the long video jobs. */}
      <ExportProgressModal busy={busy} status={status} />
    </div>
  );
}
